import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Alert,
  Stack,
  InputAdornment,
  Divider
} from '@mui/material';
import { Settings, Clock, Save, Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function GeneralSettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const [digestDelay, setDigestDelay] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/general', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDigestDelay(data.digestDelayMinutes || 10);
      }
    } catch (err) {
      console.error('Fetch settings error:', err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/general', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ digestDelayMinutes: parseInt(digestDelay) })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
    setSaving(false);
  };

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings size={28} color="#00d4aa" />
        Paramètres généraux
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Paramètres sauvegardés avec succès
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isSuperAdmin && (
        <Card sx={{ p: 3, bgcolor: 'background.paper', backgroundImage: 'none' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Bell size={20} color="#00d4aa" />
            Notifications
          </Typography>

          <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Délai du digest email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Temps d'attente avant d'envoyer un email récapitulatif des notifications.
                Les notifications sont accumulées pendant ce délai puis envoyées dans un seul email.
              </Typography>
              <TextField
                type="number"
                value={digestDelay}
                onChange={(e) => setDigestDelay(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Clock size={18} color="#00d4aa" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 1, max: 60 }
                }}
                sx={{ width: 200 }}
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Valeur recommandée : 10 minutes. Min: 1 min, Max: 60 min.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={<Save size={18} />}
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Box>
        </Card>
      )}

      {!isSuperAdmin && (
        <Typography color="text.secondary">
          Aucun paramètre disponible pour votre rôle sur cette page.
        </Typography>
      )}
    </Box>
  );
}
